import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getGenerationLabel } from '../../utils/treeLayout';

// ── Relation options by generation ─────────────────────────────────────────
const RELATION_OPTIONS = {
  '-3': ['Great Grandfather', 'Great Grandmother', 'Great Grand Uncle', 'Great Grand Aunt'],
  '-2': ['Grandfather', 'Grandmother', 'Grand Uncle', 'Grand Aunt'],
  '-1': ['Father', 'Mother', 'Uncle', 'Aunt', 'Step Father', 'Step Mother', 'Father-in-law', 'Mother-in-law'],
  '0':  ['Self', 'Wife', 'Husband', 'Brother', 'Sister', 'Cousin', 'Brother-in-law', 'Sister-in-law', 'Twin Brother', 'Twin Sister'],
  '1':  ['Son', 'Daughter', 'Nephew', 'Niece', 'Step Son', 'Step Daughter', 'Son-in-law', 'Daughter-in-law'],
  '2':  ['Grandson', 'Granddaughter', 'Grand Nephew', 'Grand Niece'],
  '3':  ['Great Grandson', 'Great Granddaughter'],
};

const AddMemberForm = ({ familyEmail, spouseTarget, defaultBranch, lockedGeneration, lockedBranch, onMemberAdded, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    gender: 'male',
    generation: Number.isFinite(lockedGeneration)
      ? String(lockedGeneration)
      : '0',
    branch: lockedBranch || defaultBranch || 'paternal',
    relation: '',
    specialNotes: '',
    parentIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allMembers, setAllMembers] = useState([]); // for parent selector

  // ── Fetch existing members for parent selector ─────────────────────────
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const res = await axios.get(
          `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(familyEmail)}`
        );
        setAllMembers(res.data.members || []);
      } catch (err) {
        console.error('Could not fetch members for parent selector:', err);
      }
    };
    fetchMembers();
  }, [familyEmail]);

  // ── Pre-fill if adding a spouse ────────────────────────────────────────
  useEffect(() => {
    if (spouseTarget) {
      setForm(f => ({
        ...f,
        generation: String(spouseTarget.generation),
        branch: spouseTarget.branch,
        gender: spouseTarget.gender === 'male' ? 'female' : 'male',
      }));
    }
  }, [spouseTarget]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── When generation changes, reset relation ────────────────────────────
  const handleGenerationChange = (val) => {
    setForm(f => ({ ...f, generation: val, relation: '', parentIds: [] }));
  };

  // ── Parent checkbox toggle ─────────────────────────────────────────────
  const toggleParent = (memberId) => {
    setForm(f => {
      const already = f.parentIds.includes(memberId);
      if (already) {
        return { ...f, parentIds: f.parentIds.filter(id => id !== memberId) };
      }
      // Max 2 parents
      if (f.parentIds.length >= 2) return f;
      return { ...f, parentIds: [...f.parentIds, memberId] };
    });
  };

  // ── Members eligible to be parents ────────────────────────────────────
  // Parent generation = one level above the member being added
  const parentGeneration = Number(form.generation) - 1;
  const eligibleParents = allMembers.filter(
    m => m.generation === parentGeneration && m.branch === form.branch
  );

  // ── Relation options for current generation ────────────────────────────
  const relationOptions = RELATION_OPTIONS[form.generation] || [];

  // ── Show parent selector for all generations except the topmost (-3) ──
  const showParentSelector = !spouseTarget && Number(form.generation) > -3;

  // ── Is generation/branch locked because this was opened via a column "+" ──
  const isLocked = Number.isFinite(lockedGeneration);

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(familyEmail)}`,
        {
          ...form,
          generation: Number(form.generation),
          parentIds: form.parentIds,
        }
      );

      // If adding a spouse — link the spouseId on both members
      if (spouseTarget) {
        const newMemberId = response.data.memberId;
        await axios.put(
          `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(familyEmail)}/${spouseTarget.memberId}`,
          { spouseId: newMemberId }
        );
        await axios.put(
          `https://familybandhan-backend-933326390640.asia-south1.run.app/api/members/${encodeURIComponent(familyEmail)}/${newMemberId}`,
          { spouseId: spouseTarget.memberId }
        );
      }

      onMemberAdded();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
      setLoading(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <h3 style={styles.title}>
          {spouseTarget ? `Add Spouse for ${spouseTarget.name}` : 'Add Family Member'}
        </h3>
        <button onClick={onClose} style={styles.closeX}>✕</button>
      </div>

      {/* Locked generation/branch info — shown when opened via column "+" */}
      {isLocked && !spouseTarget && (
        <div style={styles.lockedInfo}>
          Adding to: {getGenerationLabel(Number(lockedGeneration))} · Gen {lockedGeneration > 0 ? `+${lockedGeneration}` : lockedGeneration} · {lockedBranch === 'maternal' ? 'Maternal' : 'Paternal'}
        </div>
      )}

      {/* Name */}
      <div style={styles.field}>
        <label style={styles.label}>Full Name *</label>
        <input
          style={styles.input}
          placeholder="e.g. Kamala Devi"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          autoFocus
        />
      </div>

      {/* Gender + Branch */}
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Gender</label>
          <select style={styles.select} value={form.gender} onChange={e => set('gender', e.target.value)}>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Branch</label>
          <select
            style={styles.select}
            value={form.branch}
            onChange={e => set('branch', e.target.value)}
            disabled={!!spouseTarget || isLocked}
          >
            <option value="paternal">Paternal</option>
            <option value="maternal">Maternal</option>
          </select>
        </div>
      </div>

      {/* Generation — shown as dropdown only when NOT locked and NOT a spouse */}
      {!isLocked && !spouseTarget && (
        <div style={styles.field}>
          <label style={styles.label}>Generation</label>
          <select
            style={styles.select}
            value={form.generation}
            onChange={e => handleGenerationChange(e.target.value)}
          >
            <option value="-3">-3 — Great Grandparents</option>
            <option value="-2">-2 — Grandparents</option>
            <option value="-1">-1 — Parents</option>
            <option value="0">0 — You / Spouse</option>
            <option value="1">+1 — Children</option>
            <option value="2">+2 — Grandchildren</option>
            <option value="3">+3 — Great Grandchildren</option>
          </select>
        </div>
      )}

      {/* Relation — smart dropdown based on generation */}
      <div style={styles.field}>
        <label style={styles.label}>Relation to you</label>
        <input
          style={styles.input}
          placeholder="Select or type e.g. Paternal Uncle"
          value={form.relation}
          onChange={e => set('relation', e.target.value)}
          list="relation-options"
        />
        <datalist id="relation-options">
          {relationOptions.map(r => (
            <option key={r} value={r} />
          ))}
        </datalist>
      </div>

      {/* Parent selector — only for Gen +1 and above */}
      {showParentSelector && (
        <div style={styles.field}>
          <label style={styles.label}>
            Parents (select up to 2)
          </label>
          {eligibleParents.length === 0 ? (
            <p style={styles.noParents}>
              No members found in Gen {parentGeneration > 0 ? `+${parentGeneration}` : parentGeneration} ({form.branch}). Add parents first.
            </p>
          ) : (
            <div style={styles.parentList}>
              {eligibleParents.map(m => {
                const selected = form.parentIds.includes(m.memberId);
                return (
                  <div
                    key={m.memberId}
                    style={{
                      ...styles.parentItem,
                      background: selected ? '#f0f4ff' : '#fafafa',
                      border: selected ? '1.5px solid #6366F1' : '1.5px solid #E5E7EB',
                    }}
                    onClick={() => toggleParent(m.memberId)}
                  >
                    <div style={{
                      ...styles.parentCheckbox,
                      background: selected ? '#6366F1' : '#fff',
                      borderColor: selected ? '#6366F1' : '#D1D5DB',
                    }}>
                      {selected && <span style={{ color: '#fff', fontSize: '10px' }}>✓</span>}
                    </div>
                    <div>
                      <div style={styles.parentName}>{m.name}</div>
                      <div style={styles.parentRelation}>{m.relation || m.gender}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Hint */}
      <p style={styles.hint}>
        💡 To add a spouse, right-click the member on the tree and choose "Add spouse" instead.
      </p>

      {/* Special Notes */}
      <div style={styles.field}>
        <label style={styles.label}>Special Notes (optional, max 100 chars)</label>
        <input
          style={styles.input}
          placeholder="e.g. Retired teacher, born in Mysore"
          maxLength={100}
          value={form.specialNotes}
          onChange={e => set('specialNotes', e.target.value)}
        />
      </div>

      {error && <p style={styles.error}>{error}</p>}

      <div style={styles.actions}>
        <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={loading ? styles.submitBtnDisabled : styles.submitBtn}
        >
          {loading ? 'Adding...' : spouseTarget ? 'Add Spouse' : 'Add Member'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '28px',
    width: '420px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    fontFamily: "'DM Sans', Arial, sans-serif",
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#0e0018',
    fontFamily: "'Cormorant Garamond', serif",
  },
  closeX: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: '#9CA3AF',
  },
  lockedInfo: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#0e0018',
    background: '#FEF9C3',
    border: '1px solid #D4A017',
    borderRadius: '7px',
    padding: '8px 12px',
    marginBottom: '14px',
  },
  field: {
    marginBottom: '14px',
    flex: 1,
  },
  row: {
    display: 'flex',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: '5px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    border: '1.5px solid #E5E7EB',
    borderRadius: '7px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '9px 12px',
    border: '1.5px solid #E5E7EB',
    borderRadius: '7px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fff',
  },
  parentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  parentItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    borderRadius: '7px',
    cursor: 'pointer',
    transition: 'all 0.1s',
  },
  parentCheckbox: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: '1.5px solid #D1D5DB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  parentName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#111827',
  },
  parentRelation: {
    fontSize: '11px',
    color: '#9CA3AF',
  },
  noParents: {
    fontSize: '12px',
    color: '#9CA3AF',
    margin: '4px 0 0 0',
    fontStyle: 'italic',
  },
  hint: {
    fontSize: '11px',
    color: '#9CA3AF',
    margin: '-8px 0 10px 0',
    lineHeight: 1.5,
  },
  error: {
    color: '#DC2626',
    fontSize: '13px',
    margin: '0 0 12px 0',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '9px 18px',
    background: 'transparent',
    border: '1.5px solid #E5E7EB',
    borderRadius: '7px',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#6B7280',
  },
  submitBtn: {
    padding: '9px 18px',
    background: '#0e0018',
    color: '#ffffff',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    padding: '9px 18px',
    background: '#9CA3AF',
    color: '#ffffff',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    cursor: 'not-allowed',
  },
};

export default AddMemberForm;
